import "./LandingPageTemplates.scss";

import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Input,
  message,
  Modal,
  Popover,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import {
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileJpgOutlined,
  FileSyncOutlined,
  InfoCircleOutlined,
  TabletOutlined,
  UploadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";

import AddPageForCustomersModal from "../../Components/AddPageForCustomersModal/AddPageForCustomersModal";
import RemoveModal from "../../Components/RemoveModal/RemoveModal";
import { showAppError } from "../../Utils/error";
import { reorderItemInList } from "../../Utils/file";
import {
  createLandingPageTemplate,
  deleteLandingPageTemplate,
  editLandingPageTemplate,
  getLandingPagesTemplates,
  reorderLandingPageTemplate,
  uploadImage,
} from "../api";

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  borderRadius: 8,
  backgroundColor: isDragging ? "#f0f2f5" : "transparent",
  ...draggableStyle,
});

const LandingPageTemplates = () => {
  const [templates, setTemplates] = useState();
  const [formVisible, setFormVisible] = useState(false);
  const [selectedTempleteData, setSelectedTempleteData] = useState(null);
  const [modelUpdatedData, setModelUpdatedData] = useState({
    available: { landingPageBuilder: true, mangoPageBuilder: true },
  });
  const [file, setFile] = useState(null);
  const [reloadCount, setReloadCount] = useState(1);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [isRemoveTempVisible, setIsRemoveTempVisible] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isReorderTempVisible, setIsReorderTempVisible] = useState(false);
  const [templateReorderType, setTemplateReorderType] = useState(null);
  const [isReorderingTemp, setIsReorderingTemp] = useState(false);
  const [reorderTempList, setReorderTempList] = useState(null);

  // Add Page for Users Modal state
  const [isAddPageModalVisible, setIsAddPageModalVisible] = useState(false);

  const handleSyncTemplate = async (templateId) => {
    // console.log(isMangoPageTempReordering, setIsReorderingTemp);
    try {
      setTemplateLoading(true);
      const resp = await editLandingPageTemplate({
        templateId,
        syncTemplate: true,
      });
      if (resp?.code === 0) {
        message.success("template sync successfully");
        setReloadCount(reloadCount + 1);
      }
    } catch (error) {
      message.error(
        error?.data?.message || "Something went wrong with sync template"
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  const updateTemplateOrder = async () => {
    try {
      setIsReorderingTemp(true);
      const reqBody = {
        [templateReorderType]: reorderTempList.map((eachTemp) => eachTemp?._id),
      };

      const resp = await reorderLandingPageTemplate(reqBody);
      if (resp.code === 0) {
        message.success("Template reordered successfully");
        resetForm();
        setReorderTempList(null);
        setTemplateReorderType(null);
        setIsReorderTempVisible(false);
        setReloadCount((prev) => prev + 1);
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsReorderingTemp(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (value, record) => {
        return (
          <>
            <div className="templateName">
              <Typography.Title level={5} style={{ margin: "0 10px 0 0" }}>
                {value}{" "}
              </Typography.Title>
              {record?.isPremium ? <Tag color="#ffc233">Premium</Tag> : null}
              {record?.new ? <Tag color="#87d068">New</Tag> : null}
              <Popover
                placement="right"
                content={
                  <div>
                    <ul>
                      {record?.host ? (
                        <li>
                          <snap>{`Host: ${record?.host} `}</snap>
                        </li>
                      ) : null}
                      {record?.landingPage ? (
                        <li>
                          <snap>{`Page ID: ${record?.landingPage}`}</snap>
                        </li>
                      ) : null}
                      {record?.mangoPageOrder ? (
                        <li>
                          <snap>{`Service page order: ${record?.mangoPageOrder} `}</snap>
                        </li>
                      ) : null}
                      {record?.landingPageOrder ? (
                        <li>
                          <snap>{`Website order: ${record?.landingPageOrder}`}</snap>
                        </li>
                      ) : null}
                    </ul>
                  </div>
                }
                overlayStyle={{ width: 400 }}
              >
                <InfoCircleOutlined
                  style={{ verticalAlign: 5, fontSize: 16, color: "#65676b" }}
                />
              </Popover>
            </div>
          </>
        );
      },
    },
    {
      title: "Available for\n(Service page, Website)",
      dataIndex: "available",
      key: "available",
      width: 150,
      align: "center",
      render: (_, record) => {
        return (
          <div className="availableWrapper">
            <Checkbox checked={record?.available?.mangoPageBuilder} />
            <Checkbox checked={record?.available?.landingPageBuilder} />
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      width: 300,
      align: "center",
      render: (_, record) => {
        return (
          <div className="actionwrapper">
            <div className="actionItem">
              <Tooltip title="HTML page view">
                <Button
                  type="primary"
                  icon={<TabletOutlined />}
                  onClick={() => {
                    if (record?.previewUrl) {
                      window.open(record?.previewUrl);
                    } else {
                      message.warning("File not available");
                    }
                  }}
                />
              </Tooltip>
            </div>
            <div className="actionItem">
              <Tooltip title="Website preview img">
                <Button
                  type="primary"
                  icon={<FileJpgOutlined />}
                  onClick={() => {
                    if (record?.src) {
                      window.open(record?.src);
                    } else {
                      message.warning("File not available");
                    }
                  }}
                />
              </Tooltip>
            </div>
            <div className="actionItem">
              <Tooltip
                title={
                  record?.landingPage
                    ? "Edit template"
                    : "Edit templete diabled"
                }
              >
                <Button
                  type="primary"
                  disabled={!record?.landingPage}
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedTempleteData(record);
                    setModelUpdatedData(record);
                    setFormVisible(true);
                  }}
                />
              </Tooltip>
            </div>
            <div className="actionItem">
              <Tooltip
                title={
                  record?.landingPage
                    ? "Sync Template"
                    : "Sync Template diabled"
                }
              >
                <Button
                  type="primary"
                  disabled={!record?.landingPage}
                  icon={<FileSyncOutlined />}
                  onClick={() => {
                    handleSyncTemplate(record?._id);
                  }}
                />
              </Tooltip>
            </div>
            <div className="actionItem">
              <Tooltip title="Delete templete">
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setSelectedTempleteData(record);
                    setIsRemoveTempVisible(true);
                  }}
                />
              </Tooltip>
            </div>
          </div>
        );
      },
    },
  ];

  const resetForm = () => {
    setFile(null);
    setModelUpdatedData({
      available: { landingPageBuilder: true, mangoPageBuilder: true },
    });
    setSelectedTempleteData(null);
  };

  const handleNewTemplate = async () => {
    try {
      if (!file) {
        message.error("Upload template preview image file");
        return;
      }
      setButtonLoader(true);
      const formData = new FormData();
      formData.append("image", file);
      const imgResp = await uploadImage(formData);
      if (imgResp?.code !== 0) {
        message.error("Something went wrong in upload image");
        return;
      }
      const reqBody = {
        ...modelUpdatedData,
        src: imgResp?.result?.files?.[0],
      };
      const resp = await createLandingPageTemplate(reqBody);
      if (resp?.code === 0) {
        setReloadCount(reloadCount + 1);
        resetForm();
        setFormVisible(false);
        message.success("New template created successfully");
      } else {
        console.log(resp);
      }
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setButtonLoader(false);
    }
  };

  const handleEditTemplate = async () => {
    try {
      if (!file && !modelUpdatedData?.src) {
        message.error("Upload template preview image file");
        return;
      }
      setButtonLoader(true);
      let imgResp = null;
      let reqBody = {
        available: modelUpdatedData?.available,
        name: modelUpdatedData?.name,
        landingPageId: modelUpdatedData?.landingPage,
        isPremium: modelUpdatedData?.isPremium,
        src: modelUpdatedData?.src,
        templateId: modelUpdatedData?._id,
        new: modelUpdatedData?.new,
      };
      if (file) {
        const formData = new FormData();
        formData.append("image", file);
        imgResp = await uploadImage(formData);
        if (imgResp?.code !== 0) {
          message.error("Something went wrong in upload image");
          return;
        }
        reqBody = {
          ...reqBody,
          src: imgResp?.result?.files?.[0],
        };
      }
      const resp = await editLandingPageTemplate(reqBody);
      if (resp?.code === 0) {
        message.success("Template edited successfully");
        setReloadCount(reloadCount + 1);
        setFormVisible(false);
        resetForm();
      } else {
        console.log(resp);
      }
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setButtonLoader(false);
    }
  };

  const getTempleteList = useCallback(
    async (isReorderTemp = false) => {
      try {
        setTemplateLoading(true);
        let query = "";
        if (isReorderTemp && templateReorderType) {
          query =
            templateReorderType === "landingPageTemplates"
              ? "?type=landing"
              : "?type=mango";
        }
        const resp = await getLandingPagesTemplates(query);
        if (resp?.code === 0) {
          if (isReorderTemp) {
            setReorderTempList(resp?.result);
          } else {
            setTemplates(resp?.result);
          }
        }
      } catch (error) {
        console.log(error);
        message.error("Something went wrong");
      } finally {
        setTemplateLoading(false);
      }
    },
    [templateReorderType]
  );

  const onDeleteTemplate = async () => {
    try {
      setIsDeleteLoading(true);
      const resp = await deleteLandingPageTemplate(selectedTempleteData?._id);
      if (resp?.code === 0) {
        resetForm();
        setIsRemoveTempVisible(false);
        setReloadCount((prev) => prev + 1);
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedTempList = reorderItemInList(
      reorderTempList,
      result.source.index,
      result.destination.index
    );
    setReorderTempList(reorderedTempList);
  };

  useEffect(() => {
    getTempleteList();
  }, [reloadCount]);

  useEffect(() => {
    if (isReorderTempVisible && templateReorderType) {
      getTempleteList(true);
      console.log(isReorderTempVisible, templateReorderType);
    }
  }, [isReorderTempVisible, templateReorderType]);

  return (
    <div className="landingPageTemplatesMainWrapper">
      <div className="titleCreateBtmWrapper">
        <Typography.Title level={3}>Templates</Typography.Title>
        <div style={{ display: "flex", gap: 10 }}>
          <Dropdown
            overlay={
              <div
                style={{
                  padding: 4,
                }}
              >
                <Button
                  type="text"
                  style={{ width: "100%", textAlign: "left", border: "none" }}
                  onClick={() => {
                    setIsReorderTempVisible(true);
                    setTemplateReorderType("landingPageTemplates");
                  }}
                >
                  Website templates
                </Button>
                <Button
                  type="text"
                  style={{ width: "100%", textAlign: "left", border: "none" }}
                  onClick={() => {
                    setIsReorderTempVisible(true);
                    setTemplateReorderType("mangoPageTemplates");
                  }}
                >
                  Service page templates
                </Button>
              </div>
            }
            trigger={["hover"]}
            placement="bottomLeft"
          >
            <Button type="primary">
              Reorder templates <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setIsAddPageModalVisible(true)}
          >
            Add page for users
          </Button>
          <Button onClick={() => setFormVisible(true)}>
            Create New Template
          </Button>
        </div>
      </div>
      <div>
        <Table
          className="templateTable"
          dataSource={templates}
          columns={columns}
          loading={templateLoading}
          pagination={false}
        />
        ;
      </div>
      <Modal
        visible={formVisible}
        title={`${selectedTempleteData ? "Edit" : "Add"} Template`}
        onCancel={() => {
          setFormVisible(false);
          resetForm();
        }}
        okButtonProps={{
          loading: buttonLoader,
          name: selectedTempleteData ? "Save" : "Create",
        }}
        onOk={() => {
          if (!selectedTempleteData) {
            handleNewTemplate();
          } else {
            handleEditTemplate();
          }
        }}
      >
        <div className="modalWrapper">
          <div className="modalItemWrapper">
            <span
              style={{
                marginBottom: 8,
                display: "block",
              }}
            >
              Template ID:
            </span>
            <Input
              value={modelUpdatedData?._id}
              disabled={selectedTempleteData}
              onChange={(e) =>
                setModelUpdatedData((prev) => ({
                  ...prev,
                  landingPageId: e?.target?.value,
                }))
              }
            />
          </div>
          <div className="modalItemWrapper">
            <span
              style={{
                marginBottom: 8,
                display: "block",
              }}
            >
              Template name:
            </span>
            <Input
              value={modelUpdatedData?.name}
              onChange={(e) =>
                setModelUpdatedData((prev) => ({
                  ...prev,
                  name: e?.target?.value,
                }))
              }
            />
          </div>
          <div className="modalItemWrapper">
            <span
              style={{
                marginBottom: 8,
                display: "block",
              }}
            >
              Template available for:
            </span>
            <br />
            <Checkbox
              checked={modelUpdatedData?.available?.landingPageBuilder}
              onChange={(e) =>
                setModelUpdatedData((prev) => {
                  const { available, ...remaining } = prev;
                  return {
                    ...remaining,
                    available: {
                      ...available,
                      landingPageBuilder: e?.target?.checked,
                    },
                  };
                })
              }
            >
              Website builder
            </Checkbox>
            <Checkbox
              checked={modelUpdatedData?.available?.mangoPageBuilder}
              onChange={(e) =>
                setModelUpdatedData((prev) => {
                  const { available, ...remaining } = prev;
                  return {
                    ...remaining,
                    available: {
                      ...available,
                      mangoPageBuilder: e?.target?.checked,
                    },
                  };
                })
              }
            >
              Service page builder
            </Checkbox>
          </div>
          <div className="modalItemWrapper">
            Is a premium template?:{" "}
            <Checkbox
              checked={modelUpdatedData?.isPremium}
              onChange={(e) =>
                setModelUpdatedData((prev) => ({
                  ...prev,
                  isPremium: e.target?.checked,
                }))
              }
            />
          </div>
          <div className="modalItemWrapper">
            Is a new template?:{" "}
            <Checkbox
              checked={modelUpdatedData?.new}
              onChange={(e) =>
                setModelUpdatedData((prev) => ({
                  ...prev,
                  new: e.target?.checked,
                }))
              }
            />
          </div>
          <div className="modalItemWrapper">
            <span
              style={{
                marginBottom: 8,
                display: "block",
              }}
            >
              Upload preview template image file
            </span>
            <br />
            {file || modelUpdatedData?.src ? (
              <div className="uploadedFileName">
                <p>{file?.name || modelUpdatedData?.src}</p>
                <CloseOutlined
                  onClick={() => {
                    if (file) {
                      setFile(null);
                    } else {
                      setModelUpdatedData((prev) => {
                        const { src, ...remaining } = prev;
                        return remaining;
                      });
                    }
                  }}
                />
              </div>
            ) : (
              <Upload
                accept={[".png", ".jpg", ".jpeg"]}
                beforeUpload={(img) => setFile(img)}
                multiple={false}
              >
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            )}
          </div>
        </div>
      </Modal>
      <RemoveModal
        details={
          <p>
            Are you sure you want to delete <b>{selectedTempleteData?.name}</b>{" "}
            templete ?`
          </p>
        }
        handleOk={onDeleteTemplate}
        buttonName="Delete"
        actionButtonType="danger"
        loading={isDeleteLoading}
        visible={isRemoveTempVisible}
        setVisible={setIsRemoveTempVisible}
        title="Remove Template"
      />
      <Drawer
        title="Reorder templates"
        visible={isReorderTempVisible}
        onClose={() => {
          setReorderTempList(null);
          setIsReorderTempVisible(false);
        }}
        width={600}
        footer={[
          <Button
            type="primary"
            htmlType="submit"
            form="email-send-form"
            loading={isReorderingTemp}
            disabled={isReorderingTemp}
            onClick={updateTemplateOrder}
          >
            Reorder
          </Button>,
        ]}
        footerStyle={{
          display: "flex",
          justifyContent: "flex-end",
        }}
        destroyOnClose
      >
        <div className="customInputList">
          <DragDropContext
            onDragEnd={(result) => {
              console.log(result);
              onDragEnd(result);
            }}
          >
            <Droppable droppableId="droppable" className="droppableSection">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{
                    padding: 0,
                  }}
                >
                  {(reorderTempList || []).map((eachTemp, index) => (
                    <Draggable
                      key={eachTemp?._id}
                      draggableId={`${eachTemp?.name}_${index}_${eachTemp?._id}`}
                      index={index}
                      // isDragDisabled={isDragDisabled}
                    >
                      {(_provided, _snapshot) => (
                        <div
                          ref={_provided.innerRef}
                          {..._provided.draggableProps}
                          {..._provided.dragHandleProps}
                          style={{
                            ...getItemStyle(
                              _snapshot.isDragging,
                              _provided.draggableProps.style
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            border: "solid 1px #D3D3D3",
                            marginBottom: 8,
                          }}
                          className="customInputListItem"
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <h3 style={{ margin: 0 }}>
                              {index + 1 <= 9
                                ? `0${index + 1} `
                                : `${index + 1} `}
                              .
                            </h3>
                            <div className="templateName">
                              <Typography.Title
                                level={5}
                                style={{ margin: "0 10px 0 0" }}
                              >
                                {eachTemp?.name}{" "}
                              </Typography.Title>
                              {eachTemp?.isPremium ? (
                                <Tag color="#ffc233">Premium</Tag>
                              ) : null}
                              {eachTemp?.new ? (
                                <Tag color="#87d068">New</Tag>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </Drawer>

      <AddPageForCustomersModal
        open={isAddPageModalVisible}
        onClose={() => setIsAddPageModalVisible(false)}
      />
    </div>
  );
};

export default LandingPageTemplates;
